import { addDays, format, isValid, parseISO } from 'date-fns';
import { Effort } from '../models/effort';
import { Project, TimeType } from '../models/project';
import { TimeEntry, TimeSheet } from '../models/time-entry';

export const OFFICIAL_LEAVE_START = '2026-09-01';
export const IMPORTED_LEAVE_EFFORT_KEY = 'imported-leave';

export const IMPORTED_LEAVE_PROJECT_KEYS = {
  vacation: 'vacation-recorded',
  other: 'other-leave-recorded',
} as const;

// Imported leave exists only in the frontend. Negative IDs cannot collide with database rows,
// and these rows are excluded from timesheet saves and review updates.
export const IMPORTED_LEAVE_EFFORT_ID = -1000;
const IMPORTED_LEAVE_TIME_TYPE: TimeType = { id: 2, name: 'Non-billable' };
const IMPORTED_LEAVE_EFFORT: Effort = {
  id: IMPORTED_LEAVE_EFFORT_ID,
  key: IMPORTED_LEAVE_EFFORT_KEY,
  title: 'TAA Leave',
  isHR: true,
  tags: [],
  users: [],
  isDeleted: false,
  projects: [],
  principalGroupId: 0,
  principalGroup: { id: 0, key: '', name: '', hrCode: '', adSourceGroup: '', members: [], efforts: [] },
  ui: {},
};

function importedLeaveProject(id: number, key: string, title: string): Project {
  return {
    id, key, title,
    owner: '', customer: '', oAccount: '', mfk: '', description: '',
    hours: {}, notes: {}, review: {},
    ui: {
      groupName: IMPORTED_LEAVE_EFFORT.title, isVirtual: false, virtualId: '',
      virtualTitle: null, timeTypeId: IMPORTED_LEAVE_TIME_TYPE.id, count: 1,
    },
    tags: [],
    effort: IMPORTED_LEAVE_EFFORT,
    effortId: IMPORTED_LEAVE_EFFORT_ID,
    isDeleted: false,
    projectTimeTypes: [{ id, projectId: id, timeTypeId: IMPORTED_LEAVE_TIME_TYPE.id, timeType: IMPORTED_LEAVE_TIME_TYPE }],
    projectCustomValues: [],
  };
}

// Keyed by the API's OfficialLeaveSummary category.
const IMPORTED_LEAVE_PROJECTS_BY_CATEGORY: Record<string, Project> = {
  Vacation: importedLeaveProject(-1002, IMPORTED_LEAVE_PROJECT_KEYS.vacation, 'Vacation (imported)'),
  Other: importedLeaveProject(-1004, IMPORTED_LEAVE_PROJECT_KEYS.other, 'Other Leave (imported)'),
};

export const IMPORTED_LEAVE_PROJECTS: readonly Project[] = Object.values(IMPORTED_LEAVE_PROJECTS_BY_CATEGORY);

/** Replaces any imported leave projects from the API with the hard-coded ones, placed last. */
export function withImportedLeaveProjects(projects: Project[]): Project[] {
  return [...projects.filter(project => !isOfficialLeaveProject(project)), ...IMPORTED_LEAVE_PROJECTS];
}

export function isLegacyLeaveProject(project?: Project): boolean {
  return ['standard-leave', 'fmla-leave'].includes(project?.effort?.key ?? '');
}

export function isOfficialLeaveProject(project?: Project): boolean {
  return project?.effort?.key === IMPORTED_LEAVE_EFFORT_KEY;
}

export function isOfficialLeaveCell(project: Project, _day: string): boolean {
  return isOfficialLeaveProject(project);
}

export function isLeaveProjectVisible(project: Project, weekStart?: string): boolean {
  if (isOfficialLeaveProject(project)) return needsOfficialLeave(weekStart);
  if (isLegacyLeaveProject(project) && weekStart) return weekStart.slice(0, 10) <= OFFICIAL_LEAVE_START;
  return true;
}

export function needsOfficialLeave(weekStart?: string): boolean {
  if (!weekStart) return false;
  const start = parseISO(weekStart);
  return isValid(start) && format(addDays(start, 6), 'yyyy-MM-dd') >= OFFICIAL_LEAVE_START;
}

/**
 * Turns the API's approved official leave summaries into read-only entries on the hard-coded imported
 * projects, replacing any previously merged leave and hiding legacy leave entries after the transition week.
 * Requested leave is not shown. `projects` resolves entries loaded without their project.
 */
export function mergeOfficialLeave(timesheet: TimeSheet, projects: Project[] = []): TimeSheet {
  const start = timesheet.weekStart!.slice(0, 10);
  const timeEntries = (timesheet.timeEntries ?? []).filter(entry => {
    const project = projects.find(p => p.id === entry.projectId) ?? entry.project;
    return !project || (!isOfficialLeaveProject(project) && isLeaveProjectVisible(project, start));
  });
  if (!needsOfficialLeave(start)) return { ...timesheet, timeEntries, officialLeaveUnavailable: false };
  const summaries = timesheet.officialLeave ?? [];
  const official = new Map<string, TimeEntry>();
  for (const summary of summaries) {
    const project = IMPORTED_LEAVE_PROJECTS_BY_CATEGORY[summary.category] ?? IMPORTED_LEAVE_PROJECTS_BY_CATEGORY['Other'];
    const timeTypeId = project.projectTimeTypes[0]?.timeTypeId ?? 2;
    (summary.dailyHours ?? []).slice(0, 7).forEach((hours, day) => {
      if (!hours) return;
      // Match the API's local date-time format. A date-only string is parsed as UTC midnight by
      // new Date() and date-fns, which lands on the previous day in US timezones.
      const dateEntry = format(addDays(parseISO(start), day), "yyyy-MM-dd'T'00:00:00");
      const key = `${project.id}/${dateEntry}`;
      const entry = official.get(key) ?? {
        projectId: project.id, project, timeTypeId,
        timeType: project.projectTimeTypes[0]?.timeType,
        dateEntry, recordedTime: 0,
      };
      entry.recordedTime = (entry.recordedTime ?? 0) + hours;
      official.set(key, entry);
    });
  }
  return {
    ...timesheet,
    timeEntries: [...timeEntries, ...official.values()],
    officialLeaveUnavailable: summaries.length === 0,
  };
}
